//
// # (Expression) Evaluations
//

// Local Imports
import type { Expr } from "./expr";
import { Text } from "../graphics/text";

// # Evaluation Results
enum Kind {
  Ok = "ok", // Successful evaluation
  Error = "error", // An error (that we wanna show)
  NoDisplay = "no-display", // Do not display this result
}

type EvalResultOk = { kind: Kind.Ok; textString: string };
type EvalResultError = {
  kind: Kind.Error;
  textString: string;
};
type EvalResultNoDisplay = { kind: Kind.NoDisplay };
export type EvalResult = EvalResultOk | EvalResultError | EvalResultNoDisplay;

// # Expression Evaluation
// The combination of an expression, its evaluation context, and the result of evaluating it.
export type Eval = EvalResult & { expr: Expr };

export const Eval = {
  // Create an "ok" evaluation
  ok: (expr: Expr, textString: string): Eval & EvalResultOk => {
    return { expr, kind: Kind.Ok, textString };
  },

  // Create a "do not display" evaluation, ignoring its expression and context.
  doNotDisplay: (expr: Expr): Eval => {
    return { expr, kind: Kind.NoDisplay };
  },

  // Create an "error to be displayed" eval
  error: (expr: Expr, textString: string): Eval => {
    return { expr, kind: Kind.Error, textString };
  },

  // Get a `Text` to use to display an `Eval`.
  // This is generally used in schematic-editing contexts.
  textElem: (e: Eval): Text | null => {
    const { expr, kind } = e;

    // If the result is not to be displayed, return `null`.
    if (kind === Kind.NoDisplay) return null;

    // Combine the evaluated text-result with its `Expr` formatting.
    return Text.new({
      ...expr,
      text: e.textString,
    });
  },
};
